import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight } from "lucide-react";
import { api, wpImg } from "@/lib/api";

export default function Events() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  const upcoming = events.filter((e) => new Date(e.start_date) >= new Date());
  const past = events.filter((e) => new Date(e.start_date) < new Date());

  return (
    <div data-testid="events-page">
      <section className="relative py-28 md:py-36 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0 pattern-girih opacity-25" />
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">What's coming up</div>
          <h1 className="display text-5xl md:text-7xl mt-4 text-ink">Events & programs</h1>
        </div>
      </section>

      <section className="pb-24 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          {upcoming.length === 0 && (
            <div className="text-center py-20 text-ink/60" data-testid="no-events">
              No upcoming events at the moment. Check back soon, inshaAllah.
            </div>
          )}
          {/* 1 = centered, 2 = side-by-side, 3+ = 3-column grid on lg */}
          <div className={
            upcoming.length === 1
              ? "max-w-2xl mx-auto"
              : upcoming.length === 2
              ? "grid md:grid-cols-2 gap-6"
              : "grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          }>
            {upcoming.map((e) => <EventCard key={e.id} event={e} />)}
          </div>

          {past.length > 0 && (
            <PastEventsCarousel events={past} />
          )}
        </div>
      </section>
    </div>
  );
}

function PastEventsCarousel({ events }) {
  // Duplicate the list so the marquee loop is seamless
  const loop = [...events, ...events];
  return (
    <div className="mt-24" data-testid="past-events-carousel">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Our Journey</div>
          <h2 className="font-serif text-3xl md:text-4xl text-ink mt-2">A year in community</h2>
          <p className="text-sm text-ink/60 mt-1">Looking back at the events and moments that brought us together.</p>
        </div>
        <div className="text-xs text-ink/50 italic">Hover to pause</div>
      </div>
      <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,#000_6%,#000_94%,transparent)]">
        <div className="flex gap-6 events-marquee">
          {loop.map((e, i) => <PastEventCard key={`${e.id}-${i}`} event={e} />)}
        </div>
      </div>
    </div>
  );
}

function PastEventCard({ event }) {
  const d = new Date(event.start_date);
  return (
    <article className="shrink-0 w-72 rounded-2xl overflow-hidden bg-white border border-sand hover:shadow-xl hover:shadow-navy/10 transition-all hover:-translate-y-1">
      {event.image_url ? (
        <div className="h-44 overflow-hidden">
          <img src={wpImg(event.image_url)} alt={event.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" />
        </div>
      ) : (
        <div className="h-44 bg-gradient-to-br from-navy to-navy-900 relative pattern-girih" />
      )}
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">
          {d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </div>
        <h3 className="font-serif text-lg text-ink mt-1 line-clamp-2">{event.title}</h3>
        {event.location && <div className="text-xs text-ink/55 mt-1 truncate">{event.location}</div>}
      </div>
    </article>
  );
}

function EventCard({ event, muted }) {
  const d = new Date(event.start_date);
  return (
    <Link
      to={`/events/${event.id}`}
      data-testid={`event-card-${event.id}`}
      className={`group rounded-3xl overflow-hidden border transition-all hover:-translate-y-1 ${muted ? "bg-sand/30 border-sand opacity-80" : "bg-white border-sand hover:border-emerald_deep/30 hover:shadow-xl"}`}
    >
      {event.image_url && (
        <div className="aspect-[4/3] overflow-hidden bg-navy/5 flex items-center justify-center">
          <img src={wpImg(event.image_url)} alt={event.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700" />
        </div>
      )}
      <div className="p-7">
        <div className="flex items-center gap-4 text-xs text-ink/55 uppercase tracking-wider">
          <span className="flex items-center gap-1.5"><Calendar size={12} className="text-gold" /> {d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          {event.location && <span className="flex items-center gap-1.5"><MapPin size={12} className="text-gold" /> {event.location.split(",")[0]}</span>}
        </div>
        <h3 className="font-serif text-2xl mt-3 text-ink group-hover:text-emerald_deep transition-colors">{event.title}</h3>
        <p className="mt-2.5 text-sm text-ink/65 leading-relaxed line-clamp-3">{event.description}</p>
        <div className="mt-4 inline-flex items-center gap-1.5 text-emerald_deep text-sm font-medium">
          Details <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
